import { useEffect, useState } from "react";
import { api } from "../api/client";
import {
  offlineStatus,
  offlineRebuild,
  offlineWipe,
  offlineGetDir,
  offlineSetDir,
  pickCacheFolder,
  type OfflineStatus,
} from "../desktop/bridge";
import { useT } from "../i18n";

// Offline read-only cache controls (desktop only, §15), embedded in the Settings "Offline" tab.
// The cache is built automatically in the background after unlock (no PIN, no primary build
// button) — this screen only toggles it per device, shows freshness, sets the location, and
// offers a "Rebuild now" fallback + wipe. Offline unlock uses the same master password as login.
export function OfflineSection() {
  const t = useT();
  const [enabled, setEnabled] = useState(false);
  const [status, setStatus] = useState<OfflineStatus | null>(null);
  const [cacheDir, setCacheDir] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    api.getSelfDevice().then((d) => setEnabled(d.offline_enabled)).catch(() => undefined);
    offlineStatus().then(setStatus).catch(() => undefined);
    offlineGetDir().then(setCacheDir).catch(() => undefined);
  }, []);

  async function toggle(next: boolean) {
    setBusy(true);
    setMsg(null);
    try {
      await api.patchSelfDevice(next, true); // hardware keystore is mandatory (§15)
      setEnabled(next);
      if (!next) {
        await offlineWipe();
        setStatus(await offlineStatus());
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function rebuild() {
    setBusy(true);
    setMsg(null);
    try {
      await offlineRebuild();
      setStatus(await offlineStatus());
      setMsg(t("Offline cache refreshed."));
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function wipe() {
    setBusy(true);
    try {
      await offlineWipe();
      setStatus(await offlineStatus());
    } finally {
      setBusy(false);
    }
  }

  // Pick a new folder for the offline cache; switching location means the cache must be rebuilt
  // there (the status reads empty until the next automatic build or a manual "Rebuild now").
  async function changeDir() {
    setMsg(null);
    try {
      const picked = await pickCacheFolder(cacheDir);
      if (!picked) return;
      setCacheDir(await offlineSetDir(picked));
      setStatus(await offlineStatus());
      setMsg(t("Cache location changed — it will repopulate on the next unlock or rebuild."));
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  }
  async function resetDir() {
    setMsg(null);
    try {
      setCacheDir(await offlineSetDir(""));
      setStatus(await offlineStatus());
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  }

  const built = status?.exists ? new Date((status.built_at ?? 0) * 1000).toLocaleString() : null;
  const btn = "rounded border border-border px-2 py-1 text-xs hover:border-accent disabled:opacity-60";

  return (
    <div className="flex flex-col gap-4">
      <label className="flex items-center gap-2">
        <input type="checkbox" checked={enabled} disabled={busy} onChange={(e) => toggle(e.target.checked)} />
        {t("Enable offline read-only cache on this device")}
      </label>
      <p className="text-xs text-muted">
        {t(
          "Items marked “Allow offline” are cached automatically after you sign in, encrypted under a key protected by both your master password and this device's hardware, for 7 days. Unlock offline with the same master password; too many wrong attempts wipe the cache.",
        )}
      </p>

      {enabled && (
        <>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted">
              {built ? `${t("Cached")}: ${built}${status?.expired ? ` (${t("expired")})` : ""}` : t("No offline cache.")}
            </span>
            <button onClick={rebuild} disabled={busy} className={btn}>
              {t("Rebuild now")}
            </button>
          </div>

          <div className="flex flex-col gap-1 border-t border-border pt-3">
            <span className="text-xs font-medium">{t("Cache location")}</span>
            <code className="break-all rounded bg-panel2 px-2 py-1 text-[11px] text-muted">{cacheDir || "…"}</code>
            <div className="flex gap-2">
              <button onClick={changeDir} disabled={busy} className={btn}>
                {t("Change…")}
              </button>
              <button onClick={resetDir} disabled={busy} className={`${btn} text-muted`}>
                {t("Reset to default")}
              </button>
            </div>
          </div>

          {status?.exists && (
            <div>
              <button onClick={wipe} disabled={busy} className="rounded border border-border px-3 py-1.5 text-xs text-danger hover:border-danger">
                {t("Wipe cache")}
              </button>
            </div>
          )}
        </>
      )}

      {msg && <div className="rounded bg-panel2 px-2 py-1.5 text-xs">{msg}</div>}
    </div>
  );
}
